import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";
import type { api as ApiType } from "@Aspecto/backend/convex/_generated/api";
import type { FunctionReturnType } from "convex/server";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useCurrentCompany, useCurrentUser } from "@/hooks/use-current-account";
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Clock,
  FileText,
  FolderOpen,
  Globe,
  Lock,
  MapPin,
  MessageSquare,
  Settings,
  CircleStar,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { AppPageContent } from "@/components/layout/app-page";
import { SectionCard } from "@/components/ui/section-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGroupLabel } from "@/hooks/use-group-label";
import { cn, htmlToPlainText } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/")({
  component: OverviewPage,
});

function useClientNow() {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  return now;
}

function getGreeting(now: number | null) {
  if (now === null) return "Welcome";

  const hour = new Date(now).getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function formatRelativeTime(timestamp: number, now: number | null) {
  if (now === null) return "Recent";

  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function formatEventDate(timestamp: number, nowMs: number | null) {
  if (nowMs === null) {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(timestamp);
  }

  const date = new Date(timestamp);
  const now = new Date(nowMs);
  const tomorrow = new Date();
  tomorrow.setTime(nowMs);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === now.toDateString()) return "Today";
  if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatEventDateBadge(timestamp: number, now: number | null) {
  const date = new Date(timestamp);

  if (now === null) {
    return {
      month: date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
      day: date.getUTCDate(),
    };
  }

  return {
    month: date.toLocaleDateString(undefined, { month: "short" }),
    day: date.getDate(),
  };
}

const EVENT_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  online: Video,
  offline: MapPin,
  hybrid: Globe,
};

function OverviewSkeleton() {
  return (
    <AppPageContent variant="medium">
      <div className="mb-6">
        <Skeleton className="h-7 w-48 rounded-lg sm:h-9 sm:w-64" />
        <Skeleton className="mt-2.5 h-5 w-44 rounded-md" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 lg:gap-6">
        <div className="flex flex-col gap-4 lg:gap-6 xl:col-span-2">
          <Card>
            <CardContent className="space-y-4 !pt-5">
              <Skeleton className="h-5 w-40 rounded-md" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3.5 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4 rounded-sm" />
                    <Skeleton className="h-3 w-1/2 rounded-sm" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="lg:flex-1">
            <CardContent className="space-y-4 !pt-5">
              <Skeleton className="h-5 w-32 rounded-md" />
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4 rounded-sm" />
                    <Skeleton className="h-3 w-1/2 rounded-sm" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="hidden space-y-6 xl:block xl:col-start-3 xl:row-start-1">
          <Card>
            <CardContent className="space-y-3 !pt-5">
              <Skeleton className="h-5 w-36 rounded-md" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                  <Skeleton className="h-4 w-24 rounded-sm" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4 lg:gap-6 xl:hidden">
          <Card>
            <CardContent className="space-y-3 !pt-5">
              <Skeleton className="h-5 w-36 rounded-md" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                  <Skeleton className="h-4 w-24 rounded-sm" />
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3 !pt-5">
              <Skeleton className="h-5 w-36 rounded-md" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                  <Skeleton className="h-4 w-24 rounded-sm" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppPageContent>
  );
}

function OverviewPage() {
  const groupLabel = useGroupLabel();
  const now = useClientNow();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { data: company } = useCurrentCompany();
  const { data: upcomingEvents, isLoading: eventsLoading } = useQuery(
    convexQuery(api.events.getUpcomingEvents, {}),
  );
  const { data: latestPosts, isLoading: postsLoading } = useQuery(
    convexQuery(api.posts.getLatestPosts, {}),
  );
  const { data: groups, isLoading: groupsLoading } = useQuery(
    convexQuery(api.groups.getCompanyGroups, {}),
  );

  if (userLoading || !currentUser) {
    return <OverviewSkeleton />;
  }

  const isAdmin = currentUser.role === "company_admin";
  const firstName = currentUser.firstName;
  const myGroups = groups?.filter((g) => g.isMember || g.isGlobal) ?? [];
  const otherGroups =
    groups?.filter(
      (g) =>
        !g.isMember &&
        !g.isGlobal &&
        (g.visibility === "public" || g.visibility === "private" || g.hasPendingInvitation),
    ) ?? [];

  return (
    <AppPageContent variant="medium">
      <div className="mb-6">
        <h1 className="text-2xl font-bold sm:text-3xl">
          {getGreeting(now)}
          {firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="mt-1.5 text-muted-foreground">
          Here's what's happening in {company?.name ?? "your organisation"}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 lg:gap-6">
        <div className="flex flex-col gap-4 lg:gap-6 xl:col-span-2">
          <UpcomingEvents
            events={upcomingEvents ?? []}
            isLoading={eventsLoading}
            groupLabel={groupLabel}
            now={now}
          />
          <div className="lg:flex-1">
            <LatestUpdates
              posts={latestPosts ?? []}
              isLoading={postsLoading}
              groupLabel={groupLabel}
              now={now}
            />
          </div>
        </div>

        {/* xl sidebar: stacked in col 3 */}
        <div className="hidden space-y-6 xl:block xl:col-start-3 xl:row-start-1">
          <YourCommunities groups={myGroups} isLoading={groupsLoading} groupLabel={groupLabel} />
          <JoinableCommunitiesCard groups={otherGroups} groupLabel={groupLabel} />
          {isAdmin && <AdminBlock groupLabel={groupLabel} />}
        </div>

        <div className="flex flex-col gap-4 lg:gap-6 xl:hidden">
          <YourCommunities groups={myGroups} isLoading={groupsLoading} groupLabel={groupLabel} />
          <JoinableCommunitiesCard groups={otherGroups} groupLabel={groupLabel} />
          {isAdmin && (
            <div className="lg:flex-1">
              <AdminBlock groupLabel={groupLabel} />
            </div>
          )}
        </div>
      </div>
    </AppPageContent>
  );
}

type UpcomingEvent = FunctionReturnType<typeof ApiType.events.getUpcomingEvents>[number];

function UpcomingEvents({
  events,
  isLoading,
  groupLabel,
  now,
}: {
  events: UpcomingEvent[];
  isLoading: boolean;
  groupLabel: { singular: string; plural: string; singularLower: string; pluralLower: string };
  now: number | null;
}) {
  return (
    <SectionCard
      title="Upcoming events"
      description={`Events across your ${groupLabel.pluralLower}`}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3.5 rounded-lg border p-4">
              <Skeleton className="h-12 w-12 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4 rounded-sm" />
                <Skeleton className="h-3 w-1/2 rounded-sm" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No upcoming events"
          description={`Events from your ${groupLabel.pluralLower} will appear here.`}
        />
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const TypeIcon = EVENT_TYPE_ICONS[event.eventType] ?? Globe;
            const eventDate = formatEventDateBadge(event.eventDate, now);
            return (
              <Link
                key={event._id}
                to="/app/groups/$groupId"
                params={{ groupId: event.groupId }}
                search={{ tab: "events" }}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="flex items-start gap-3 rounded-lg border p-3 transition-all duration-200 hover:bg-accent/50 hover:shadow-sm sm:gap-4 sm:p-4">
                  <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-12 sm:w-12">
                    <span className="text-xs font-medium leading-none">{eventDate.month}</span>
                    <span className="text-lg font-bold leading-tight">{eventDate.day}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{event.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:gap-x-3">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatEventDate(event.eventDate, now)} at {event.eventTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <TypeIcon className="h-3 w-3" />
                        {event.eventType}
                      </span>
                      <span className="flex items-center gap-1">
                        <CircleStar className="h-3 w-3" />
                        {event.attendeeCount} attending
                      </span>
                    </div>
                    <Badge variant="outline" className="mt-2 text-[10px]">
                      {event.groupName}
                    </Badge>
                  </div>
                  {event.isAttending && (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Going
                    </Badge>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

type LatestPost = FunctionReturnType<typeof ApiType.posts.getLatestPosts>[number];

function LatestUpdates({
  posts,
  isLoading,
  groupLabel,
  now,
}: {
  posts: LatestPost[];
  isLoading: boolean;
  groupLabel: { singular: string; plural: string; singularLower: string; pluralLower: string };
  now: number | null;
}) {
  return (
    <SectionCard
      title="Latest updates"
      description={`Recent updates from your ${groupLabel.pluralLower}`}
    >
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3.5">
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4 rounded-sm" />
                <Skeleton className="h-3 w-full rounded-sm" />
                <Skeleton className="h-3 w-1/2 rounded-sm" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No updates yet"
          description={`Updates from your ${groupLabel.pluralLower} will show up here.`}
        />
      ) : (
        <ul className="space-y-4">
          {posts.map((post) => {
            const authorName = post.author
              ? `${post.author.firstName ?? ""} ${post.author.lastName ?? ""}`.trim() || "Someone"
              : "Someone";
            return (
              <li key={post._id}>
                <Link
                  to="/app/groups/$groupId"
                  params={{ groupId: post.groupId }}
                  search={{ tab: "posts" }}
                  className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div className="flex items-start gap-3 rounded-lg border p-2.5 transition-all duration-200 hover:bg-muted/50 sm:gap-3.5 sm:p-3">
                    {post.author?.imageUrl ? (
                      <Avatar className="h-8 w-8 shrink-0 sm:h-9 sm:w-9">
                        <AvatarImage src={post.author.imageUrl} />
                        <AvatarFallback>{post.author.firstName?.[0] ?? "U"}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted sm:h-9 sm:w-9">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium">{authorName}</p>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatRelativeTime(post.createdAt, now)}
                        </span>
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                        {htmlToPlainText(post.content)}
                      </p>
                      <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">
                          {post.groupName}
                        </Badge>
                        {post.commentCount > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {post.commentCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function YourCommunities({
  groups,
  isLoading,
  groupLabel,
}: {
  groups: Array<{
    _id: Id<"groups">;
    name: string;
    description?: string;
    isGlobal: boolean;
    visibility?: "public" | "private" | "secret";
    isMember: boolean;
  }>;
  isLoading: boolean;
  groupLabel: { singular: string; plural: string; singularLower: string; pluralLower: string };
}) {
  return (
    <SectionCard
      title={`Your ${groupLabel.pluralLower}`}
      description={
        isLoading
          ? undefined
          : `${groups.length} ${groups.length !== 1 ? groupLabel.pluralLower : groupLabel.singularLower}`
      }
      action={
        <Link to="/app/groups" className="cursor-pointer">
          <Button variant="ghost" size="sm" className="cursor-pointer">
            View all
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </Link>
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
              <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-24 rounded-sm" />
                <Skeleton className="h-3 w-32 rounded-sm" />
              </div>
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title={`No ${groupLabel.pluralLower} yet`}
          description={`Join or create a ${groupLabel.singularLower} to get started.`}
        />
      ) : (
        <ul className="space-y-2">
          {groups.slice(0, 5).map((group) => (
            <li key={group._id}>
              <Link
                to="/app/groups/$groupId"
                params={{ groupId: group._id }}
                className="block cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="flex items-center gap-3 rounded-lg border p-3 transition-all duration-200 hover:bg-accent/50 hover:shadow-sm">
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                      "bg-muted",
                    )}
                  >
                    {group.isGlobal ? (
                      <Globe className="h-4.5 w-4.5 text-muted-foreground" />
                    ) : group.visibility === "secret" ? (
                      <CircleStar className="h-4.5 w-4.5 text-muted-foreground" />
                    ) : group.visibility === "private" ? (
                      <Lock className="h-4.5 w-4.5 text-muted-foreground" />
                    ) : (
                      <CircleStar className="h-4.5 w-4.5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{group.name}</p>
                    {group.description && (
                      <p className="truncate text-xs text-muted-foreground">{group.description}</p>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function JoinableCommunitiesCard({
  groups,
  groupLabel,
}: {
  groups: Array<{
    _id: Id<"groups">;
    name: string;
    description?: string;
    hasPendingInvitation?: boolean;
  }>;
  groupLabel: { singular: string; plural: string; singularLower: string; pluralLower: string };
}) {
  return (
    <SectionCard title={groupLabel.plural} description={`${groupLabel.plural} you can join`}>
      {groups.length === 0 ? (
        <div className="flex flex-col items-center py-4 text-center">
          <FolderOpen className="mb-2 h-6 w-6 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">
            You've joined all {groupLabel.pluralLower}!
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {groups.slice(0, 3).map((group) => (
            <li key={group._id}>
              <Link
                to="/app/groups/$groupId"
                params={{ groupId: group._id }}
                className="block cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 transition-all duration-200 hover:border-solid hover:bg-accent/50">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <CircleStar className="h-4.5 w-4.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{group.name}</p>
                    {group.description && (
                      <p className="truncate text-xs text-muted-foreground">{group.description}</p>
                    )}
                  </div>
                  {group.hasPendingInvitation && (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Invited
                    </Badge>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function AdminBlock({
  groupLabel,
}: {
  groupLabel: { singular: string; plural: string; singularLower: string; pluralLower: string };
}) {
  const adminLinks = [
    {
      to: "/app/groups" as const,
      icon: FolderOpen,
      label: `Manage ${groupLabel.pluralLower}`,
      description: "Create & organise",
    },
    {
      to: "/app/analytics" as const,
      icon: BarChart3,
      label: "View analytics",
      description: "Insights & reports",
    },
    {
      to: "/app/admin" as const,
      icon: Settings,
      label: "Organisation settings",
      description: "Branding & config",
    },
  ];
  return (
    <SectionCard title="Admin" description="Manage your organisation">
      <div className="space-y-2">
        {adminLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="block cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="flex items-center gap-3 rounded-lg border p-3 transition-all duration-200 hover:bg-accent/50 hover:shadow-sm">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <link.icon className="h-4.5 w-4.5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{link.label}</p>
                <p className="text-xs text-muted-foreground">{link.description}</p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </Link>
        ))}
      </div>
    </SectionCard>
  );
}
